/**
 * Generates a random verification code of specified length (default: 6 digits)
 * @param length Length of the verification code to generate
 * @returns A string containing the random code
 */
export function generateVerificationCode(length: number = 6): string {
  // Create a random number with the specified number of digits
  const min = Math.pow(10, length - 1);
  const max = Math.pow(10, length) - 1;
  return Math.floor(min + Math.random() * (max - min + 1)).toString();
}

/**
 * Formats a verification code with spaces for better readability
 * @param code The verification code to format
 * @param groupSize How many digits to group together (default: 3)
 * @returns A formatted string (e.g., "123 456" for groupSize 3)
 */
export function formatVerificationCode(code: string, groupSize: number = 3): string {
  if (!code) return '';
  
  // Remove any existing spaces
  const cleanCode = code.replace(/\s/g, '');
  
  // Group the digits
  const groups = [];
  for (let i = 0; i < cleanCode.length; i += groupSize) {
    groups.push(cleanCode.substring(i, i + groupSize));
  }
  
  return groups.join(' ');
}

/**
 * Checks if a verification code has expired
 * @param createdAt When the code was created
 * @param expiresInHours How many hours the code is valid for (default: 24)
 * @returns Boolean indicating if the code has expired
 */
export function isVerificationCodeExpired(
  createdAt: Date,
  expiresInHours: number = 24
): boolean {
  if (!createdAt) return true;
  
  const now = new Date();
  const expiryTime = new Date(createdAt.getTime() + expiresInHours * 60 * 60 * 1000);
  
  return now > expiryTime;
}

/**
 * Calculates time remaining before a verification code expires
 * @param createdAt When the code was created
 * @param expiresInHours How many hours the code is valid for (default: 24)
 * @returns Object with hours, minutes and seconds remaining, or null if expired
 */
export function getVerificationTimeRemaining(
  createdAt: Date,
  expiresInHours: number = 24
): { hours: number; minutes: number; seconds: number } | null {
  if (!createdAt) return null;
  
  const now = new Date();
  const expiryTime = new Date(createdAt.getTime() + expiresInHours * 60 * 60 * 1000);
  
  // If already expired
  if (now > expiryTime) return null;
  
  // Calculate remaining time in milliseconds
  const diff = expiryTime.getTime() - now.getTime();
  
  // Convert to hours, minutes, seconds
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  
  return { hours, minutes, seconds };
} 